import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography,
  Alert,
  Link as MuiLink
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AuthorizationClient from '../single-page/api/AuthorizationClient.js';
import { TextMaskDocument, TextMaskPhone, TextMaskName } from './TextMaskCustom.jsx';
import grafismo from '../../assets/images/grafismo.svg';
import { useModals } from '@/hooks/useModals.jsx';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from "../single-page/constants/routes.js";
import { useUser } from '../hooks/useUser';

const inputStyles = {
  marginBottom: '12px',
  width: {
    xs: '95%',
    sm: '90%',
    md: '80%'
  },
  '& .MuiOutlinedInput-root': {
    border: '1.68px solid #012F4740',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      border: 'none'
    },
    '&:hover': {
      borderColor: '#012F47'
    },
    '&.Mui-focused': {
      borderColor: '#012F47'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#012F47'
    }
  },
  '& .MuiInputLabel-outlined': {
    '&.MuiInputLabel-shrink': {
      background: 'white',
      padding: '0 8px',
      marginLeft: '-4px',
      transform: 'translate(14px, -9px) scale(0.75)'
    }
  }
};

const NewHereModal = () => {
  const navigate = useNavigate();
  const { checkAuth } = useUser();
  const { 
    registerOpen, 
    handleCloseRegister, 
    handleOpenLogin,
    licensePlate,
    planType
  } = useModals();

  const [user, setUser] = useState({
    name: '',
    last_name: '',
    email: '',
    document: '',
    phone: '',
    password: '',
    password_confirmation: '',
    license_plate: ''
  });
  
  const [userErrors, setUserErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    if (licensePlate) {
      setUser(prev => ({
        ...prev,
        license_plate: licensePlate
      }));
    }
  }, [licensePlate]);

  const handleChange = (e) => {
    if (e?.target?.name) {
      setUser(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    } 
    else if (typeof e === 'object' && e.name) {
      setUser(prev => ({
        ...prev,
        [e.name]: e.value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setUserErrors({});
    setErrorMessage('');
    
    try {
      const userData = {
        ...user,
        ...(licensePlate && { license_plate: licensePlate }),
        ...(planType && { plan_type: planType })
      };

      const response = await AuthorizationClient.signUp(userData);
      
      await checkAuth();
      
      handleCloseRegister();

      if (response.inspection?.inspection_id) {
        navigate(`${ROUTES.MY_INSPECTIONS}/${response.inspection.inspection_id}`);
      } else {
        navigate(ROUTES.MY_INSPECTIONS);
      }

    } catch (error) {
      if (error.data) {
        setUserErrors(error.data);
        if (error.data.email?.includes('já está em uso')) {
          setErrorMessage('Este e-mail já está cadastrado.');
          setShowLoginPrompt(true);
        } else {
          setErrorMessage('Ocorreu um erro inesperado. Por favor, tente novamente.');
        } 
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getTextFieldProps = (name, label, mask = null) => ({
    name,
    label,
    value: user[name],
    onChange: handleChange,
    required: true,
    error: !!userErrors[name],
    helperText: userErrors[name]?.join(', '),
    sx: inputStyles,
    autoComplete: "off",
    ...(mask && {
      InputProps: {
        inputComponent: mask,
        inputProps: {
          name,
          onChange: handleChange
        }
      }
    })
  });

  const handleLoginClick = () => {
    handleCloseRegister();
    handleOpenLogin();
  };

  return (
    <Dialog 
      open={registerOpen}
      onClose={handleCloseRegister}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: { 
          borderRadius: 16,
          margin: '16px',
          position: 'relative',
          borderTop: '4px solid #51DF94',
          boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
        }
      }}
    >
      <Grid container>
        <Grid 
          item 
          xs={2}
          sm={3}
          md={4}
          sx={{ 
            backgroundColor: '#012F47',
            minHeight: { 
              xs: '150px',
              sm: '400px',
              md: '500px'
            },
            position: 'relative',
            display: 'block'
          }}
        >
          <Box
            component="img"
            src={grafismo}
            alt="Grafismo"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              objectFit: 'contain'
            }}
          />
        </Grid>

        <Grid 
          item 
          xs={10}
          sm={9}
          md={8}
          style={{ position: 'relative' }}
        >
          <IconButton
            onClick={handleCloseRegister}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ 
            p: { 
              xs: 2,
              sm: 3,
              md: 4  
            },
            pt: { 
              xs: 4,
              sm: 5 
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {errorMessage && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  showLoginPrompt && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => handleLoginClick()}
                      sx={{ 
                        textTransform: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Fazer login
                    </Button>
                  )
                }
              >
                {errorMessage}
              </Alert>
            )}

            {showSuccess && (
              <Alert 
                severity="success"
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}

            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3,
                textAlign: 'center',
                fontWeight: 600,
                fontSize: {
                  xs: '1.5rem',
                  sm: '2rem'
                }
              }}
            >
              Novo por aqui?
            </Typography>

            <form 
              onSubmit={handleSubmit} 
              style={{ 
                width: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}
              autoComplete="off"
            >
              <TextField
                {...getTextFieldProps('name', 'Nome', TextMaskName)}
              />
              
              <TextField
                {...getTextFieldProps('last_name', 'Sobrenome')}
              />

              <TextField
                {...getTextFieldProps('document', 'CPF ou CNPJ', TextMaskDocument)}
              />

              <TextField
                {...getTextFieldProps('phone', 'Celular', TextMaskPhone)}
              />

              <TextField
                {...getTextFieldProps('email', 'E-mail')}
              />

              <TextField
                {...getTextFieldProps('password', 'Senha')}
                type="password"
                autoComplete="new-password"
              />

              <TextField
                {...getTextFieldProps('password_confirmation', 'Confirme a senha')}
                type="password"
                autoComplete="new-password"
              />

              <Box sx={{ 
                mt: 3,
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 1, sm: 2 },
                width: { xs: '95%', sm: '90%', md: '80%' },
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Button 
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  sx={{ 
                    bgcolor: '#4CAF50',
                    padding: { xs: '10px 20px', sm: '12px 40px' },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: '#388E3C',
                    }
                  }}
                >
                  {isLoading ? 'Enviando...' : 'Enviar'}
                </Button>

                <Button 
                  type="button"
                  onClick={handleLoginClick}
                  variant="outlined"
                  disabled={isLoading}
                  sx={{ 
                    color: '#012F47',
                    borderColor: '#012F47',
                    padding: { xs: '10px 20px', sm: '12px 40px' },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#012F47',
                      backgroundColor: 'rgba(1, 47, 71, 0.04)'
                    }
                  }}
                >
                  Já tenho conta
                </Button>
              </Box>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default NewHereModal;